/**
 * Portfolio Health trend: which stored points the chart shows for a range, and
 * where they sit on a real time axis.
 *
 * Points are one per calendar day (YYYY-MM-DD) with independent percentages;
 * they are drawn unstacked. A point with nothing in any bucket comes from a day
 * with no engagements and is not health data, so it is left off the chart.
 */

export type TrendRange = '30d' | '90d' | '6m';

export interface TrendPoint {
  date: string;
  onTrack: number;
  needsAttention: number;
  atRisk: number;
  total?: number;
}

export interface PlottedTrendPoint extends TrendPoint {
  /** Local midnight of `date`, in ms: the chart's x value. */
  ts: number;
}

export const RANGE_DAYS: Record<TrendRange, number> = { '30d': 30, '90d': 90, '6m': 183 };

const DAY_MS = 24 * 60 * 60 * 1000;

/** Local midnight for a YYYY-MM-DD string; NaN for anything else (legacy "MMM DD" labels). */
export function dateToLocalMs(date: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(date ?? '').trim());
  if (!m) return NaN;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getTime();
}

export function isEmptyTrendPoint(p: Pick<TrendPoint, 'onTrack' | 'needsAttention' | 'atRisk'>): boolean {
  return (Number(p.onTrack) || 0) + (Number(p.needsAttention) || 0) + (Number(p.atRisk) || 0) === 0;
}

function localMidnight(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** [first day, today] of the range as local-midnight ms: the chart's x domain. */
export function trendDomain(range: TrendRange, today: Date = new Date()): [number, number] {
  const end = localMidnight(today);
  const start = new Date(end);
  start.setDate(start.getDate() - (RANGE_DAYS[range] - 1));
  return [localMidnight(start), end];
}

/** Stored points worth drawing for the range, oldest first, each with its x value. */
export function visibleTrend(points: TrendPoint[] | null | undefined, range: TrendRange, today: Date = new Date()): PlottedTrendPoint[] {
  const [start, end] = trendDomain(range, today);
  return (Array.isArray(points) ? points : [])
    .filter((p) => p && !isEmptyTrendPoint(p))
    .map((p) => ({ ...p, ts: dateToLocalMs(p.date) }))
    .filter((p) => !Number.isNaN(p.ts) && p.ts >= start && p.ts < end + DAY_MS)
    .sort((a, b) => a.ts - b.ts);
}

/** Days with real health data in the whole history, whatever the range. */
export function recordedTrendDays(points: TrendPoint[] | null | undefined): number {
  return (Array.isArray(points) ? points : []).filter((p) => p && !isEmptyTrendPoint(p) && !Number.isNaN(dateToLocalMs(p.date))).length;
}

export function formatTrendTick(ts: number): string {
  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function formatTrendLabel(ts: number): string {
  return new Date(ts).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}
